import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Trash2, Loader2, Bot, User } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { modules } from '../data/modules';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSection(pathname) {
  if (pathname.startsWith('/mbs')) return 'mbs';
  if (pathname.startsWith('/sops') || pathname === '/login') return 'sops';
  if (pathname.startsWith('/k4') || pathname.startsWith('/module') || pathname === '/search' || pathname === '/chat') return 'training';
  if (pathname.startsWith('/equipment')) return 'equipment';
  if (pathname.startsWith('/preparation')) return 'preparation';
  if (pathname.startsWith('/consent')) return 'consent';
  if (pathname.startsWith('/screening')) return 'screening';
  if (pathname.startsWith('/incidents')) return 'incidents';
  if (pathname.startsWith('/radiation-governance')) return 'radgov';
  if (pathname.startsWith('/dias-checklist') || pathname.startsWith('/compliance')) return 'compliance';
  if (pathname.startsWith('/referrals')) return 'referrals';
  if (pathname.startsWith('/people')) return 'people';
  return null; // Dashboard / Management / Practice settings / Contracts
}

function getSectionConfig(section) {
  const configs = {
    training:    { name: 'K4 Resource Assist',     color: '#2563EB', placeholder: 'Ask about K4...',                    sessionKey: 'k4_chat_training' },
    mbs:         { name: 'MBS Monitor Assist',     color: '#16A34A', placeholder: 'Ask about MBS fees, billing...',     sessionKey: 'k4_chat_mbs' },
    sops:        { name: 'SOPs Assist',            color: '#7C3AED', placeholder: 'Ask about SOPs, audits...',          sessionKey: 'k4_chat_sops' },
    equipment:   { name: 'Equipment Assist',       color: '#0891B2', placeholder: 'Ask about an asset, QA, service…',   sessionKey: 'k4_chat_equipment' },
    preparation: { name: 'Patient Prep Assist',    color: '#42CDD7', placeholder: 'Ask about a prep guide…',            sessionKey: 'k4_chat_prep' },
    consent:     { name: 'Consent Assist',         color: '#7C3AED', placeholder: 'Ask about a consent template…',      sessionKey: 'k4_chat_consent' },
    screening:   { name: 'Screening Assist',       color: '#42CDD7', placeholder: 'Ask about a screening form…',        sessionKey: 'k4_chat_screening' },
    incidents:   { name: 'Incident Assist',        color: '#DC2626', placeholder: 'Ask about an incident or RCA…',      sessionKey: 'k4_chat_incidents' },
    radgov:      { name: 'Radiation Governance',   color: '#D97706', placeholder: 'Ask about TLD, aprons, DRLs…',       sessionKey: 'k4_chat_radgov' },
    compliance:  { name: 'DIAS / QIP Assist',      color: '#16A34A', placeholder: 'Ask about DIAS modules, QIP…',       sessionKey: 'k4_chat_compliance' },
    referrals:   { name: 'Referral Base Assist',   color: '#0E2D5C', placeholder: 'Ask about a referrer or fax…',       sessionKey: 'k4_chat_referrals' },
    people:      { name: 'People Assist',           color: '#0EA5E9', placeholder: 'Ask about staff, licensing, onboarding…', sessionKey: 'k4_chat_people' },
  };
  return configs[section] || configs.training;
}

function getSessionId(section) {
  const key = getSectionConfig(section).sessionKey;
  let sid = localStorage.getItem(key);
  if (!sid) { sid = `${section}_` + Math.random().toString(36).substring(2, 15) + Date.now().toString(36); localStorage.setItem(key, sid); }
  return sid;
}

function formatMessage(text) {
  if (!text) return '';
  let h = text;
  h = h.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  h = h.replace(/`(.+?)`/g, '<code class="text-xs bg-sky-50 text-[#42CDD7] px-1 py-0.5 rounded">$1</code>');
  h = h.replace(/^(\d+)\.\s(.+)$/gm, '<div class="flex gap-2 items-start py-0.5"><span class="w-5 h-5 rounded-full bg-sky-100 text-[#42CDD7] text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">$1</span><span>$2</span></div>');
  h = h.replace(/^[-•]\s(.+)$/gm, '<div class="flex gap-2 items-start py-0.5"><span class="w-1.5 h-1.5 rounded-full bg-[#42CDD7] shrink-0 mt-2"></span><span>$1</span></div>');
  h = h.replace(/\n\n/g, '<br/><br/>'); h = h.replace(/\n/g, '<br/>');
  return h;
}

const SECTION_TOPICS = {
  training: 'Karisma K4', mbs: 'MBS fees & billing', sops: 'SOPs & compliance',
  equipment: 'equipment, QA & service', preparation: 'patient preparation',
  consent: 'consent templates', screening: 'screening forms',
  incidents: 'incidents & RCA', radgov: 'radiation governance',
  compliance: 'DIAS / QIP readiness', referrals: 'referrers & faxing',
  people: 'staff, licensing & onboarding',
};

const SECTION_SUGGESTIONS = {
  training:    ["How do I register a patient?", "Request is missing from my worklist", "How to create a report template?"],
  mbs:         ["How does MBS billing work?", "What are the private patient fee methods?", "How do I set up Medicare Bulk Bill?"],
  sops:        ["What antiseptic do we use for biopsies?", "Anaphylaxis protocol — key steps?", "Audit schedule: what's monthly vs quarterly?", "When is Workcover invoiced?"],
  equipment:   ["Which assets are due for service this month?", "Show me equipment with QA overdue.", "Bulk-import a new equipment list — how?"],
  preparation: ["CT contrast prep — what fasting rules?", "MRI — claustrophobia screening tips", "Diabetic patient prep — key alerts"],
  consent:     ["Montgomery consent — what's required?", "Steroid injection consent — key risks", "When does a patient need a driver?"],
  screening:   ["MRI screening — pacemakers / implants", "Pregnancy screening for X-ray", "Contrast allergy screening"],
  incidents:   ["What classifies as a near-miss?", "RCA template — key steps", "When to escalate to RANZCR?"],
  radgov:      ["TLD reading — what's a normal range?", "Lead apron testing frequency?", "DRL — how to record an exceedance"],
  compliance:  ["What's missing for QIP audit?", "DIAS module 4 — workforce checks", "How do I attest a manual check?"],
  referrals:   ["Add a new referrer", "How to fax a report?", "Mark referrer as inactive"],
  people:      ["Whose AHPRA expires next month?", "Show staff missing 2FA setup", "How do I assign SOPs to a new radiographer?"],
};

export default function ChatBubble() {
  const location = useLocation();
  const section = getSection(location.pathname);
  const config = section ? getSectionConfig(section) : null;
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [lastSection, setLastSection] = useState(section);
  const lastUserMsgRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = section ? getSessionId(section) : '';

  // Reset when section changes
  useEffect(() => {
    if (section !== lastSection) { setMessages([]); setHistoryLoaded(false); setLastSection(section); }
  }, [section, lastSection]);

  // Scroll so the user's last question sits near the top of the viewport —
  // means the answer grows downward naturally and you don't have to scroll up
  // to find the question you just asked. Called ONLY after sending a new user
  // message; assistant answers don't auto-scroll.
  const scrollUserMsgToTop = useCallback(() => {
    lastUserMsgRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  useEffect(() => {
    if (isOpen && !historyLoaded) loadHistory();
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen, historyLoaded]);

  // External "ask-assistant" event — allows other pages to open + pre-fill the chat
  useEffect(() => {
    const handler = (e) => {
      const question = e.detail?.question;
      setIsOpen(true);
      if (question) {
        // slight delay so panel is mounted + history loaded
        setTimeout(() => sendMessage(null, question), 350);
      }
    };
    window.addEventListener('ask-assistant', handler);
    return () => window.removeEventListener('ask-assistant', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const loadHistory = async () => {
    try { const res = await axios.get(`${API}/chat/history/${sessionId}`); if (res.data.length) setMessages(res.data.map(m => ({ role: m.role, content: m.content }))); } catch {}
    setHistoryLoaded(true);
  };

  const sendMessage = async (e, directMsg) => {
    if (e) e.preventDefault();
    const userMsg = directMsg || input.trim();
    if (!userMsg || loading) return;
    setInput(''); setMessages(prev => [...prev, { role: 'user', content: userMsg }]); setLoading(true);
    // Scroll the newly-posted user message to the top of the chat viewport so
    // the answer renders below it — user doesn't have to scroll back up.
    setTimeout(scrollUserMsgToTop, 80);
    try {
      const moduleMatch = location.pathname.match(/^\/module\/([^/]+)/);
      const moduleSlug = moduleMatch ? moduleMatch[1] : null;
      const currentModule = moduleSlug ? modules.find(m => m.id === moduleSlug) : null;
      const res = await axios.post(`${API}/chat`, { message: userMsg, session_id: sessionId, section, module_context: currentModule?.title || null });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch { setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]); }
    setLoading(false);
    // Intentionally NOT scrolling to bottom after assistant response — the user
    // can scroll down themselves to read the full answer at their own pace.
  };

  const clearChat = async () => { try { await axios.post(`${API}/chat/clear/${sessionId}`); setMessages([]); } catch {} };

  if (location.pathname === '/chat') return null;
  if (!section) return null; // Dashboard / Management / Compliance / Contracts — no assistant
  const suggestions = SECTION_SUGGESTIONS[section] || SECTION_SUGGESTIONS.training;

  return (
    <>
      {!isOpen && (
        <button data-testid="chat-bubble-btn" onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-6 z-50 w-14 h-14 text-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all hover:scale-105 group"
          style={{ backgroundColor: config.color }}>
          <Bot className="w-6 h-6" />
          <span className="absolute -top-10 right-0 bg-slate-900 text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">{config.name}</span>
        </button>
      )}

      {isOpen && (
        <div data-testid="chat-panel" className="fixed bottom-20 right-6 z-50 w-[640px] h-[840px] max-h-[90vh] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" style={{ maxWidth: 'calc(100vw - 32px)' }}>
          <div className="text-white px-4 py-3 flex items-center justify-between shrink-0" style={{ background: `linear-gradient(135deg, ${config.color} 0%, #0d2644 100%)` }}>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"><Bot className="w-4 h-4" /></div>
              <div><h3 className="text-sm font-semibold font-['Outfit']">{config.name}</h3><p className="text-[10px] opacity-75">GPT-5.2 | i3 Radiology</p></div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid="chat-clear-btn" onClick={clearChat} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors" title="Clear"><Trash2 className="w-4 h-4" /></button>
              <button data-testid="chat-close-btn" onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: `${config.color}15` }}>
                  <Bot className="w-6 h-6" style={{ color: config.color }} />
                </div>
                <h4 className="font-['Outfit'] text-sm font-semibold text-foreground mb-1">{config.name}</h4>
                <p className="text-xs text-muted-foreground mb-4">Ask me anything about {SECTION_TOPICS[section] || 'this section'}.</p>
                <div className="space-y-2">
                  {suggestions.map((q, i) => (
                    <button key={i} data-testid={`chat-suggestion-${i}`} onClick={() => sendMessage(null, q)}
                      className="block w-full text-left px-3 py-2 text-xs text-foreground/80 bg-muted hover:bg-accent hover:text-[#42CDD7] rounded-lg transition-colors border border-border">{q}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => {
              const isLastUser = msg.role === 'user' && i === messages.length - (loading ? 1 : 2);
              return (
              <div key={i} ref={isLastUser ? lastUserMsgRef : null}
                className={`flex gap-2.5 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${msg.role === 'user' ? 'bg-slate-200' : ''}`} style={msg.role !== 'user' ? { backgroundColor: `${config.color}15` } : {}}>
                  {msg.role === 'user' ? <User className="w-3.5 h-3.5 text-foreground/80" /> : <Bot className="w-3.5 h-3.5" style={{ color: config.color }} />}
                </div>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${msg.role === 'user' ? 'text-white rounded-tr-md' : 'bg-muted text-foreground rounded-tl-md'}`}
                  style={msg.role === 'user' ? { backgroundColor: config.color } : {}}>
                  {msg.role === 'user' ? <span>{msg.content}</span> : <div dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />}
                </div>
              </div>
            );})}
            {loading && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${config.color}15` }}><Bot className="w-3.5 h-3.5" style={{ color: config.color }} /></div>
                <div className="bg-muted rounded-2xl rounded-tl-md px-4 py-3"><div className="flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: config.color }} /><span className="text-xs text-muted-foreground">Thinking...</span></div></div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendMessage} className="p-3 border-t border-border shrink-0">
            <div className="flex gap-2">
              <input ref={inputRef} data-testid="chat-input" type="text" value={input} onChange={(e) => setInput(e.target.value)}
                placeholder={config.placeholder} disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm bg-muted border border-border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-50"
                style={{ '--tw-ring-color': `${config.color}30` }} />
              <button type="submit" data-testid="chat-send-btn" disabled={loading || !input.trim()}
                className="px-3 py-2.5 text-white rounded-xl transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed"
                style={{ backgroundColor: loading || !input.trim() ? undefined : config.color }}>
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
